<template>
    <div class="fin-mb-5 fin-border fin-rounded fin-p-5 fin-bg-white">

        <h3>Fast Track Quote Config</h3>

        <el-form :model="config">

            <el-form-item label="Quote Enabled Globally">
                <small class="fin-block">This option can be overwritten at the form level</small>
                <el-select class="fin-w-full" v-model="config.enabled" placeholder="Quote Enabled Globally" filterable>
                    <el-option label="Yes" value="1"></el-option>
                    <el-option label="No" value="0"></el-option>
                </el-select>
            </el-form-item>

            <el-form-item label="Lender Icons">
                <small class="fin-block">Show lender icons in Fast Track Quote</small>
                <el-select class="fin-w-full" v-model="config.icons" placeholder="Lender Icons" filterable>
                    <el-option label="Yes" value="1"></el-option>
                    <el-option label="No" value="0"></el-option>
                </el-select>
            </el-form-item>

            <el-form-item label="Custom Description">
                <small class="fin-block">Overwrite the quote description</small>
                <el-select class="fin-w-full" v-model="config.customDescription" placeholder="Overwrite Description" filterable>
                    <el-option label="Yes" value="1"></el-option>
                    <el-option label="No" value="0"></el-option>
                </el-select>
            </el-form-item>

            <el-form-item label="Quote Description">
                <editor v-if="config.customDescription == '1'" class="fin-w-full" v-model="config.description" :api-key="apiKey" :init="init"></editor>
                <div class="fin-w-full fin-flex" v-else v-html="config.description"></div>
            </el-form-item>

            <el-form-item label="Custom Disclaimer">
                <small class="fin-block">Overwrite the quote disclaimer</small>
                <el-select class="fin-w-full" v-model="config.customDisclaimer" placeholder="Overwrite Disclaimer" filterable>
                    <el-option label="Yes" value="1"></el-option>
                    <el-option label="No" value="0"></el-option>
                </el-select>
            </el-form-item>

            <el-form-item label="Quote Disclaimer">
                <editor v-if="config.customDisclaimer == '1'" class="fin-w-full" v-model="config.disclaimer" :api-key="apiKey" :init="init"></editor>
                <div class="fin-w-full fin-flex" v-else v-html="config.disclaimer"></div>
            </el-form-item>

            <el-form-item label="Quote Generation Method">
                <el-select class="fin-w-full" v-model="config.method" placeholder="Quote Generation Method" filterable>
                    <el-option v-for="item in methodTypes" :key="item.value" :label="item.label" :value="item.value"></el-option>
                </el-select>
            </el-form-item>

            <template v-if="config.method == 'dynamic'">
                <h6>
                    Select below the consumer and commercial lenders you wish to appear in the quote. The system with select the
                    three best tiers from the below lenders.
                </h6>

                <div class="fin-w-full fin-flex fin-gap-5">
                    <div class="fin-w-1/2">
                        <el-form-item label="Consumer Lenders">
                            <el-select
                                class="fin-w-full"
                                v-model="config.dynamic.consumer"
                                placeholder="Consumer Lenders"
                                filterable
                                multiple
                            >
                                <el-option
                                    v-for="item in consumerLenders"
                                    :key="item.value"
                                    :label="item.label"
                                    :value="item.value"
                                ></el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                    <div class="fin-w-1/2">
                        <el-form-item label="Commerical Lenders">
                            <el-select
                                class="fin-w-full"
                                v-model="config.dynamic.commercial"
                                placeholder="Commercial Lenders"
                                filterable
                                multiple
                            >
                                <el-option
                                    v-for="item in commercialLenders"
                                    :key="item.value"
                                    :label="item.label"
                                    :value="item.value"
                                ></el-option>
                            </el-select>
                        </el-form-item>
                    </div>
                </div>
            </template>

            <template v-else-if="config.method == 'manual'">

                <h6>Select below the consumer and commercial lender tiers below</h6>

                <div class="fin-w-full d-flex fin-gap-5">

                    <div class="fin-w-1/2">

                        <label>Consumer Lenders</label>

                        <div class="fin-w-full fin-flex fin-gap-5">

                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 1 Lender">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.consumer.quoteOne.lender"
                                        placeholder="Consumer Lenders"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in consumerLenders"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 1 Tier">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.consumer.quoteOne.tier"
                                        placeholder="Consumer Tiers"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in tiers.consumer.quoteOne"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>

                        <div class="fin-w-full fin-flex fin-gap-5">

                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 2 Lender">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.consumer.quoteTwo.lender"
                                        placeholder="Consumer Lenders"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in consumerLenders"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 2 Tier">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.consumer.quoteTwo.tier"
                                        placeholder="Consumer Tiers"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in tiers.consumer.quoteTwo"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>

                        <div class="fin-w-full fin-flex fin-gap-5">

                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 3 Lender">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.consumer.quoteThree.lender"
                                        placeholder="Consumer Lenders"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in consumerLenders"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 3 Tier">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.consumer.quoteThree.tier"
                                        placeholder="Consumer Tiers"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in tiers.consumer.quoteThree"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>
                    </div>

                    <div class="fin-w-1/2">

                        <label>Commercial Lenders</label>

                        <div class="fin-w-full fin-flex fin-gap-5">

                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 1 Lender">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.commercial.quoteOne.lender"
                                        placeholder="Commercial Lenders"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in commercialLenders"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 1 Tier">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.commercial.quoteOne.tier"
                                        placeholder="Commercial Tiers"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in tiers.commercial.quoteOne"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>

                        <div class="fin-w-full fin-flex fin-gap-5">

                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 2 Lender">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.commercial.quoteTwo.lender"
                                        placeholder="Commercial Lenders"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in commercialLenders"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 2 Tier">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.commercial.quoteTwo.tier"
                                        placeholder="Commercial Tiers"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in tiers.commercial.quoteTwo"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>

                        <div class="fin-w-full fin-flex fin-gap-5">

                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 3 Lender">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.commercial.quoteThree.lender"
                                        placeholder="Commercial Lenders"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in commercialLenders"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="fin-w-1/2">
                                <el-form-item label="Quote 3 Tier">
                                    <el-select
                                        class="fin-w-full"
                                        v-model="config.manual.commercial.quoteThree.tier"
                                        placeholder="Commercial Tiers"
                                        filterable
                                        clearable>
                                        <el-option
                                            v-for="item in tiers.commercial.quoteThree"
                                            :key="item.value"
                                            :label="item.label"
                                            :value="item.value"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </el-form>
    </div>
</template>

<script>

import Editor from "@tinymce/tinymce-vue"

export default {
    name: "FormBuilderConfig",

    components: {
        Editor,
    },

    props: {
        lenders: {
            required: true,
        },
    },

    data() {
        return {
            loadingConfig: true,
            saveTimeout: null,
            saveTimeoutWait: 2000,
            methodTypes: [
                {
                    label: "Dynamic",
                    value: "dynamic",
                },
                {
                    label: "Manual",
                    value: "manual",
                },
            ],
            tiers: {
                consumer: {
                    quoteOne: [],
                    quoteTwo: [],
                    quoteThree: [],
                },
                commercial: {
                    quoteOne: [],
                    quoteTwo: [],
                    quoteThree: [],
                }
            },
            config: {
                enabled: "1",
                method: "dynamic",
                icons: "0",
                description: "",
                customDescription: "0",
                disclaimer: "",
                customDisclaimer: "0",
                dynamic: {
                    consumer: [],
                    commercial: [],
                },
                manual: {
                    consumer: {
                        quoteOne: {
                            lender: null,
                            tier: null,
                        },
                        quoteTwo: {
                            lender: null,
                            tier: null,
                        },
                        quoteThree: {
                            lender: null,
                            tier: null,
                        }
                    },
                    commercial: {
                        quoteOne: {
                            lender: null,
                            tier: null,
                        },
                        quoteTwo: {
                            lender: null,
                            tier: null,
                        },
                        quoteThree: {
                            lender: null,
                            tier: null,
                        }
                    }
                },
            },
        }
    },

    computed: {
        apiKey() {
            return process.env.MIX_TINY_MCE_API
        },
        init() {
            return {
                height: 400,
                menubar: false,
                plugins: [
                    "advlist autolink link image lists charmap print preview hr pagebreak spellchecker",
                    "searchreplace wordcount code fullscreen insertdatetime media nonbreaking",
                    "table directionality emoticons template paste",
                ],
                noneditable_editable_class: "noneditable",
                visual: false,
                auto_focus: false,
                relative_urls: false,
                remove_script_host: false,
                toolbar:
                    "undo redo | formatselect | bold italic forecolor backcolor fontsizeselect | code alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat",
            }
        },
        consumerLenders() {
            if (_.isEmpty(this.lenders.consumer)) return []
            let consumerLenders = []
            Object.entries(this.lenders.consumer).forEach((consumerLender) => {
                consumerLenders.push({
                    value: consumerLender[0],
                    label: consumerLender[1],
                })
            })
            return consumerLenders
        },
        commercialLenders() {
            if (_.isEmpty(this.lenders.commercial)) return []
            let commercialLenders = []
            Object.entries(this.lenders.commercial).forEach((commercialLender) => {
                commercialLenders.push({
                    value: commercialLender[0],
                    label: commercialLender[1],
                })
            })
            return commercialLenders
        },
    },

    mounted() {
        this.getQuoteSettings()
    },

    methods: {
        getQuoteSettings() {
            this.loadingConfig = true
            this.$tenant
                .post(`/api/forms/get-quote-settings`, {
                    teamId: Ambition.currentTeamId()
                })
                .then((res) => {
                    this.config.enabled = res.data.quoteEnabled || this.config.enabled
                    this.config.method = res.data.quoteMethod || this.config.method
                    this.config.description = res.data.quoteDescription || this.config.description
                    this.config.customDescription = res.data.customQuoteDescription || this.config.customDescription
                    this.config.disclaimer = res.data.quoteDisclaimer || this.config.disclaimer
                    this.config.customDisclaimer = res.data.customQuoteDisclaimer || this.config.customDisclaimer
                    this.config.icons = res.data.lenderIcons || this.config.icons
                    this.config.dynamic.consumer = res.data.lendersConsumer || this.config.dynamic.consumer
                    this.config.dynamic.commercial = res.data.lendersCommercial || this.config.dynamic.commercial
                    this.config.manual.consumer = res.data.manualTiersConsumer || this.config.manual.consumer
                    this.config.manual.commercial = res.data.manualTiersCommercial || this.config.manual.commercial
                })
                .catch((err) => console.error(err))
                .finally(() => {
                    this.loadingConfig = false
                })
        }
    },

    watch: {
        config: {
            handler(config, oldConfig) {
                if (!this.loadingConfig) {
                    if (this.saveTimeout) clearTimeout(this.saveTimeout)
                    this.saveTimeout = setTimeout(() => {

                        this.$tenant
                            .post(`/api/forms/save-quote-settings`, {
                                teamId: Ambition.currentTeamId(),
                                config: config
                            })
                            .then((res) => {
                                this.config.disclaimer = _.get(res, 'data.original.quoteDisclaimer')
                                this.config.description = _.get(res, 'data.original.quoteDescription')
                            })
                            .catch((err) => console.error(err))
                    }, this.saveTimeoutWait)
                }
            },
            deep: true,
        },

        'config.manual.consumer.quoteOne.lender': {
            handler(lender, currentLender) {
                if (lender !== currentLender && !_.isNull(currentLender)) {
                    this.config.manual.consumer.quoteOne.tier = null
                    this.tiers.consumer.quoteOne = []
                }
                if (lender) {
                    this.$tenant.post(`/api/forms/get-lender-tiers`, {
                        lender: lender,
                        type: 1
                    }, {
                        abort: false
                    })
                    .then(res => {
                        let tiers = []
                        if (!_.isEmpty(res.data)) {
                            Object.entries(res.data).forEach((tier) => {
                                tiers.push({
                                    value: tier[0],
                                    label: tier[1],
                                })
                            })
                        }
                        this.tiers.consumer.quoteOne = tiers
                    })
                }
            },
            immediate: true
        },

        'config.manual.consumer.quoteTwo.lender': {
            handler(lender, currentLender) {
                if (lender !== currentLender && !_.isNull(currentLender)) {
                    this.config.manual.consumer.quoteTwo.tier = null
                    this.tiers.consumer.quoteTwo = []
                }
                if (lender) {
                    this.$tenant.post(`/api/forms/get-lender-tiers`, {
                        lender: lender,
                        type: 1
                    }, {
                        abort: false
                    })
                    .then(res => {
                        let tiers = []
                        if (!_.isEmpty(res.data)) {
                            Object.entries(res.data).forEach((tier) => {
                                tiers.push({
                                    value: tier[0],
                                    label: tier[1],
                                })
                            })
                        }
                        this.tiers.consumer.quoteTwo = tiers
                    })
                }
            },
            immediate: true
        },
        'config.manual.consumer.quoteThree.lender': {
            handler(lender, currentLender) {
                if (lender !== currentLender && !_.isNull(currentLender)) {
                    this.config.manual.consumer.quoteThree.tier = null
                    this.tiers.consumer.quoteThree = []
                }
                if (lender) {
                    this.$tenant.post(`/api/forms/get-lender-tiers`, {
                        lender: lender,
                        type: 1
                    }, {
                        abort: false
                    })
                    .then(res => {
                        let tiers = []
                        if (!_.isEmpty(res.data)) {
                            Object.entries(res.data).forEach((tier) => {
                                tiers.push({
                                    value: tier[0],
                                    label: tier[1],
                                })
                            })
                        }
                        this.tiers.consumer.quoteThree = tiers
                    })
                }
            },
            immediate: true
        },

        'config.manual.commercial.quoteOne.lender': {
            handler(lender, currentLender) {
                if (lender !== currentLender && !_.isNull(currentLender)) {
                    this.config.manual.commercial.quoteOne.tier = null
                    this.tiers.commercial.quoteOne = []
                }
                if (lender) {
                    this.$tenant.post(`/api/forms/get-lender-tiers`, {
                        lender: lender,
                        type: 2
                    }, {
                        abort: false
                    })
                    .then(res => {
                        let tiers = []
                        if (!_.isEmpty(res.data)) {
                            Object.entries(res.data).forEach((tier) => {
                                tiers.push({
                                    value: tier[0],
                                    label: tier[1],
                                })
                            })
                        }
                        this.tiers.commercial.quoteOne = tiers
                    })
                }
            },
            immediate: true
        },

        'config.manual.commercial.quoteTwo.lender': {
            handler(lender, currentLender) {
                if (lender !== currentLender && !_.isNull(currentLender)) {
                    this.config.manual.commercial.quoteTwo.tier = null
                    this.tiers.commercial.quoteTwo = []
                }
                if (lender) {
                    this.$tenant.post(`/api/forms/get-lender-tiers`, {
                        lender: lender,
                        type: 2
                    }, {
                        abort: false
                    })
                    .then(res => {
                        let tiers = []
                        if (!_.isEmpty(res.data)) {
                            Object.entries(res.data).forEach((tier) => {
                                tiers.push({
                                    value: tier[0],
                                    label: tier[1],
                                })
                            })
                        }
                        this.tiers.commercial.quoteTwo = tiers
                    })
                }
            },
            immediate: true
        },
        'config.manual.commercial.quoteThree.lender': {
            handler(lender, currentLender) {
                if (lender !== currentLender && !_.isNull(currentLender)) {
                    this.config.manual.commercial.quoteThree.tier = null
                    this.tiers.commercial.quoteThree = []
                }
                if (lender) {
                    this.$tenant.post(`/api/forms/get-lender-tiers`, {
                        lender: lender,
                        type: 2
                    }, {
                        abort: false
                    })
                    .then(res => {
                        let tiers = []
                        if (!_.isEmpty(res.data)) {
                            Object.entries(res.data).forEach((tier) => {
                                tiers.push({
                                    value: tier[0],
                                    label: tier[1],
                                })
                            })
                        }
                        this.tiers.commercial.quoteThree = tiers
                    })
                }
            },
            immediate: true
        },
    },
}
</script>

<style lang="scss" scoped>
    .el-form-item :deep {
        .el-form-item__content {
            .tox-tinymce {
                @apply fin-w-full #{!important}
            }
        }
    }
</style>
